import { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Controller, useFormContext, useFieldArray, useFormState } from 'react-hook-form';
import sortBy from 'lodash.sortby';
import { styled } from '@mui/material/styles';
import Autocomplete from '@components/Autocomplete';
import FieldWithActions, { rowActions } from '@components/FieldWithActions';
import { googleActions } from '@actions';
import { MAX_ADDITIONAL_CATEGORIES } from '@constants/companies';
import { useTranslation, useThrottleValue } from '@hooks';

const Container = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
});

const CategoriesForm = ({ className, fieldsName }) => {
    const { t } = useTranslation();
    const { control, setValue, watch } = useFormContext();
    const { isSubmitting } = useFormState({ control });
    const { searchedCategories } = useSelector(state => state.google);

    const [searches, setSearches] = useState([]);
    const dispatch = useDispatch();

    const { fields, append, remove } = useFieldArray({
        control,
        name: fieldsName,
    });

    const primaryCategory = watch('primaryCategory');

    useEffect(() => {
        const primaryCategory = { id: 'primary', search: '' };
        const additionalCategories = fields.map(field => ({ id: field.id, search: '' }));
        setSearches([primaryCategory, ...additionalCategories]);
    }, [fields]);

    const setSearch = (id, value) => {
        setSearches(prev => prev.map(searchObj => (searchObj.id === id ? { ...searchObj, search: value } : searchObj)));
    };

    const firstNonEmptySearch = useMemo(
        () => searches.find(search => search.search.length > 0)?.search ?? '',
        [searches],
    );
    const throttledSearch = useThrottleValue(firstNonEmptySearch, 1000);

    useEffect(() => {
        if (throttledSearch.length === 0) {
            dispatch(googleActions.resetSearchedCategories());
            return;
        }
        if (throttledSearch.length >= 2) {
            dispatch(googleActions.getSearchedCategories(throttledSearch));
        }
    }, [throttledSearch]);

    const availableCategories = useMemo(() => {
        return sortBy(
            searchedCategories.filter(
                category =>
                    !fields.some(field => field.category[0]?.id === category.id) &&
                    primaryCategory[0]?.id !== category.id,
            ),
            'displayName',
        );
    }, [searchedCategories, fields, primaryCategory]);

    const isAtMax = fields.length >= MAX_ADDITIONAL_CATEGORIES;

    return (
        <Container className={className}>
            <FieldWithActions
                actions={rowActions({
                    index: 0,
                    count: fields.length + 1,
                    onAdd: () => append({ category: '' }),
                    canAdd: !isAtMax,
                    canDelete: false,
                    addLabel: t('buttons.addCategory'),
                    disabled: isSubmitting,
                    t,
                })}
            >
                <Controller
                    control={control}
                    name="primaryCategory"
                    render={({ field: { ref, ...field }, fieldState: { error } }) => (
                        <Autocomplete
                            {...field}
                            inputRef={ref}
                            label={t('Listings.primaryCategory')}
                            options={availableCategories}
                            value={field.value}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            optionLabelProp="displayName"
                            disabled={isSubmitting}
                            inputProps={{
                                value: searches.find(search => search.id === 'primary')?.search ?? '',
                                onChange: e => setSearch('primary', e.target.value),
                                style: { display: field?.value?.length > 0 ? 'none' : 'block' },
                            }}
                            onChangeAutoComplete={items => {
                                setValue('primaryCategory', items.length ? items : [], {
                                    shouldDirty: true,
                                });
                                setSearch('primary', '');
                            }}
                            required
                            error={error}
                        />
                    )}
                />
            </FieldWithActions>
            {fields.map((formField, index) => (
                <FieldWithActions
                    key={formField.id}
                    actions={rowActions({
                        index: index + 1,
                        count: fields.length + 1,
                        onAdd: () => append({ category: '' }),
                        onDelete: () => remove(index),
                        canAdd: !isAtMax,
                        canDelete: true,
                        addLabel: t('buttons.addCategory'),
                        disabled: isSubmitting,
                        t,
                    })}
                >
                    <Controller
                        control={control}
                        name={`${fieldsName}[${index}].category`}
                        render={({ field: { ref, ...field } }) => (
                            <Autocomplete
                                {...field}
                                inputRef={ref}
                                label={t('Listings.secondaryCategory', { count: 1 })}
                                options={availableCategories}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                optionLabelProp="displayName"
                                disabled={isSubmitting}
                                inputProps={{
                                    value: searches.find(search => search.id === formField.id)?.search ?? '',
                                    onChange: e => setSearch(formField.id, e.target.value),
                                    style: { display: field?.value?.length > 0 ? 'none' : 'block' },
                                }}
                                onChangeAutoComplete={items => {
                                    setValue(`${fieldsName}[${index}].category`, items.length ? items : [], {
                                        shouldDirty: true,
                                    });
                                    setSearch(formField.id, '');
                                }}
                            />
                        )}
                    />
                </FieldWithActions>
            ))}
        </Container>
    );
};

export default CategoriesForm;
